// store/slices/brandsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { REACT_APP_API, brandsEndpoint } from "../consts/consts";
import { logoutUser } from "./usersSlice";


/* ================= INITIAL STATE ================= */

const initialState = {
  allBrands: [],
  brandsAdmin: [],
  brandAdminGet: {},
  loading: false,
  error: null,
}

/* ================= THUNKS ================= */

export const fetchBrands = createAsyncThunk(
  "brands/fetchAll",
  async (token, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${REACT_APP_API}brands`, {
        headers: { "x-access-token": token },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchBrandsAdmin = createAsyncThunk(
  "brands/fetchAllAdmin",
  async (token, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${REACT_APP_API}brands`, {
        headers: { "x-access-token": token },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchBrandAdmin = createAsyncThunk(
  "brands/fetchAdminGet",
  async ({ id, token }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${REACT_APP_API}brands/${id}`, {
        headers: { "x-access-token": token },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);


export const addBrand = createAsyncThunk(
  "brands/add",
  async ({ brand, token }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${REACT_APP_API}brands/add`,
        brand,
        { headers: { "x-access-token": token } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateBrand = createAsyncThunk(
  "brands/update",
  async ({ brand, token }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${REACT_APP_API}brands/update`,
        brand,
        { headers: { "x-access-token": token } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteBrand = createAsyncThunk(
  "brands/delete",
  async ({ id, token }, { rejectWithValue }) => {
    try {
      await axios.delete(`${brandsEndpoint}delete/${id}`, {
        headers: { "x-access-token": token },
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/* ================= SLICE ================= */

const brandsSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {
    clearBrandError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrands.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.allBrands = action.payload;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchBrandsAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBrandsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.brandsAdmin = action.payload;
      })
      .addCase(fetchBrandsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchBrandAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBrandAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.brandAdminGet = action.payload;
      })
      .addCase(fetchBrandAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addBrand.fulfilled, (state, action) => {
        state.brandsAdmin.push(action.payload);
        localStorage.setItem("brandAdded", true);
      })

      .addCase(deleteBrand.pending, (state) => {
        state.loading = true;
      })

      .addCase(deleteBrand.fulfilled, (state, action) => {
        state.allBrands = state.allBrands.filter(
          (b) => b.id !== action.payload
        );
        localStorage.setItem("brandDeleted", true);
      })

      .addCase(deleteBrand.rejected, (state, action) => {
        localStorage.setItem("brandDeleted", action.payload);
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateBrand.pending, (state) => {
        state.loading = true;
      })

      .addCase(updateBrand.fulfilled, (state, action) => {
        state.brandsAdmin.push(action.payload);
        localStorage.setItem("brandUpdated", true);
      })

      .addCase(updateBrand.rejected, (state, action) => {
        state.loading = false;
        localStorage.setItem("brandUpdated", action.payload)
        state.error = action.payload;
      })

      .addCase(logoutUser, () => initialState);

  },
});

export const { clearBrandError } = brandsSlice.actions;
export default brandsSlice.reducer;
